import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Popconfirm, message, Typography, Card, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Delivery } from '../../types/delivery';
import { deliveryService } from '../../services/deliveryService';
import DeliveryForm from './DeliveryForm';

const { Title } = Typography;

const DeliveriesPage: React.FC = () => {
  const { t } = useTranslation();
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | undefined>(undefined);
  const [switchLoading, setSwitchLoading] = useState<{ [key: number]: boolean }>({});

  // Function to fetch all deliveries
  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await deliveryService.getAllDeliveries();
      setDeliveries(data);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      message.error(t('deliveries.notifications.error', { message: (error as Error).message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load deliveries on component mount
  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  // Handle editing a delivery
  const handleEdit = (delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setFormVisible(true);
  };

  // Handle creating a new delivery
  const handleCreate = () => {
    setSelectedDelivery(undefined);
    setFormVisible(true);
  };

  // Handle deleting a delivery
  const handleDelete = async (id: number) => {
    try {
      await deliveryService.deleteDelivery(id);
      message.success(t('deliveries.notifications.delete_success'));
      fetchDeliveries();
    } catch (error) {
      console.error('Error deleting delivery:', error);
      message.error(t('deliveries.notifications.error', { message: (error as Error).message }));
    }
  };

  // Handle toggle default status
  const handleDefaultToggle = async (delivery: Delivery, checked: boolean) => {
    setSwitchLoading(prev => ({ ...prev, [delivery.id]: true }));
    try {
      await deliveryService.updateDelivery(delivery.id, {
        name: delivery.name,
        domain: delivery.domain,
        is_default: checked
      });
      message.success(t('deliveries.notifications.update_success'));
      fetchDeliveries(); // Refresh the list to show updated states
    } catch (error) {
      console.error('Error updating delivery default status:', error);
      message.error(t('deliveries.notifications.error', { message: (error as Error).message }));
    } finally {
      setSwitchLoading(prev => ({ ...prev, [delivery.id]: false }));
    }
  };

  // After successful form submission, refresh the list
  const handleFormSuccess = () => {
    fetchDeliveries();
  };

  // Table columns
  const columns = [
    {
      title: t('deliveries.list.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string | null) => text || '-'
    },
    {
      title: t('deliveries.list.domain'),
      dataIndex: 'domain',
      key: 'domain',
    },
    {
      title: t('deliveries.list.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: t('deliveries.list.updated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: t('deliveries.list.default'),
      dataIndex: 'is_default',
      key: 'is_default',
      align: 'center' as const,
      render: (isDefault: boolean, record: Delivery) => (
        <Switch
          checked={isDefault}
          loading={switchLoading[record.id] || false}
          onChange={(checked) => handleDefaultToggle(record, checked)}
          size="small"
        />
      ),
    },
    {
      title: t('deliveries.list.actions'),
      key: 'actions',
      render: (_: any, record: Delivery) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            {t('deliveries.actions.edit')}
          </Button>
          <Popconfirm
            title={t('deliveries.actions.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              {t('deliveries.actions.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title={t('deliveries.list.title')}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div></div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          {t('deliveries.actions.create')}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={deliveries}
        rowKey="id"
        loading={loading}
      />

      <DeliveryForm
        delivery={selectedDelivery}
        isVisible={formVisible}
        onClose={() => setFormVisible(false)}
        onSuccess={handleFormSuccess}
      />
    </Card>
  );
};

export default DeliveriesPage; 